<script lang="ts">
	import { Button, Text, Toggle } from '@Shelly/shelly-design-system';
	import { format } from 'svelte-i18n';
	import { getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { real_id } from 'src/helpers/interface_helpers';
	import type { VentoraFanConfig, VentoraIdleRefreshConfig } from 'src/types/ventora_fan';
	import VentoraFanNumericInput from './VentoraFanNumericInput.svelte';
	import {
		VENTORA_IDLE_REFRESH_PRIORITY_HINT,
		VENTORA_IDLE_SPEED_HINT,
		VENTORA_REFRESH_DURATION_HINT,
		VENTORA_REFRESH_INTERVAL_HINT,
		VENTORA_REFRESH_SPEED_HINT,
	} from './ventora_fan_field_copy';
	import {
		defaultVentoraFanConfig,
		patchVentoraFanConfig,
		resolveVentoraFanConfig,
		VENTORA_FAN_COMPONENT_KEY,
		ventoraClamp,
		ventoraFiniteNumberFromInput,
		ventoraHasValidationErrors,
		ventoraIdleRefreshLimits,
		ventoraNumericValidation,
		ventoraToNumber,
	} from './ventora_fan_settings_helpers';

	export let device;
	export let isOffline: boolean | undefined;
	export let test_setting: boolean | undefined;
	export let useNewLayout: boolean | undefined;

	const id = real_id(device.id);

	type IdleNumericField = keyof Pick<
		VentoraIdleRefreshConfig,
		'idle_speed' | 'speed' | 'interval' | 'duration'
	>;

	let loading = false;
	let dirty = false;
	let draft: VentoraIdleRefreshConfig = { ...defaultVentoraFanConfig.idle_refresh };

	$: settingsStore = getDeviceSettings(device.id);
	$: config = resolveVentoraFanConfig(
		$settingsStore?.[VENTORA_FAN_COMPONENT_KEY] as Partial<VentoraFanConfig> | undefined,
	);

	$: if (!dirty) {
		draft = { ...config.idle_refresh };
	}

	$: idleSpeedValidation = ventoraNumericValidation(
		draft.idle_speed,
		...ventoraIdleRefreshLimits.idle_speed,
	);
	$: refreshSpeedValidation = ventoraNumericValidation(draft.speed, ...ventoraIdleRefreshLimits.speed);
	$: refreshIntervalValidation = ventoraNumericValidation(
		draft.interval,
		...ventoraIdleRefreshLimits.interval,
	);
	$: refreshDurationValidation = ventoraNumericValidation(
		draft.duration,
		...ventoraIdleRefreshLimits.duration,
	);
	$: hasValidationErrors = ventoraHasValidationErrors([
		idleSpeedValidation,
		refreshSpeedValidation,
		refreshIntervalValidation,
		refreshDurationValidation,
	]);

	const inputField = (field: IdleNumericField, e: Event) => {
		dirty = true;
		const n = ventoraFiniteNumberFromInput((e.currentTarget as HTMLInputElement).value);
		if (n !== undefined) draft = { ...draft, [field]: n };
	};

	const blurField = (field: IdleNumericField, e: Event) => {
		const [min, max] = ventoraIdleRefreshLimits[field];
		const raw = (e.currentTarget as HTMLInputElement).value;
		draft = {
			...draft,
			[field]: ventoraClamp(ventoraToNumber(raw, draft[field]), min, max),
		};
	};

	const commitIdleDraft = () => {
		draft = {
			...draft,
			idle_speed: ventoraClamp(draft.idle_speed, ...ventoraIdleRefreshLimits.idle_speed),
			speed: ventoraClamp(draft.speed, ...ventoraIdleRefreshLimits.speed),
			interval: ventoraClamp(draft.interval, ...ventoraIdleRefreshLimits.interval),
			duration: ventoraClamp(draft.duration, ...ventoraIdleRefreshLimits.duration),
		};
	};

	const save = async () => {
		if (!dirty || hasValidationErrors) return;
		commitIdleDraft();
		loading = true;
		const ok = await patchVentoraFanConfig(device, { idle_refresh: { ...draft } });
		if (ok) dirty = false;
		loading = false;
	};
</script>

<Text styleType="body-small" class="mb-m text-color-muted">
	{$format(VENTORA_IDLE_REFRESH_PRIORITY_HINT.key, {
		default: VENTORA_IDLE_REFRESH_PRIORITY_HINT.default,
	})}
</Text>

<VentoraFanNumericInput
	_id="input-ventora-idle-speed"
	labelKey="ventora_idle_speed"
	labelDefault="Idle speed"
	hintKey={VENTORA_IDLE_SPEED_HINT.key}
	hintDefault={VENTORA_IDLE_SPEED_HINT.default}
	value={draft.idle_speed}
	min={ventoraIdleRefreshLimits.idle_speed[0]}
	max={ventoraIdleRefreshLimits.idle_speed[1]}
	icon="fas fa-fan"
	validation={idleSpeedValidation}
	onInput={(e) => inputField('idle_speed', e)}
	onBlur={(e) => blurField('idle_speed', e)}
/>

<Toggle
	size="m"
	class="mb-m"
	checked={draft.enable}
	on:click={() => {
		draft = { ...draft, enable: !draft.enable };
		dirty = true;
	}}
	id="toggle-ventora-idle-refresh"
>
	{$format('ventora_idle_refresh_enable', { default: 'Enable periodic refresh' })}
</Toggle>

<VentoraFanNumericInput
	_id="input-ventora-refresh-speed"
	labelKey="ventora_refresh_speed"
	labelDefault="Refresh speed"
	hintKey={VENTORA_REFRESH_SPEED_HINT.key}
	hintDefault={VENTORA_REFRESH_SPEED_HINT.default}
	value={draft.speed}
	min={ventoraIdleRefreshLimits.speed[0]}
	max={ventoraIdleRefreshLimits.speed[1]}
	icon="fas fa-wind"
	validation={refreshSpeedValidation}
	onInput={(e) => inputField('speed', e)}
	onBlur={(e) => blurField('speed', e)}
/>

<VentoraFanNumericInput
	_id="input-ventora-refresh-interval"
	labelKey="ventora_refresh_interval"
	labelDefault="Refresh interval (seconds)"
	hintKey={VENTORA_REFRESH_INTERVAL_HINT.key}
	hintDefault={VENTORA_REFRESH_INTERVAL_HINT.default}
	value={draft.interval}
	min={ventoraIdleRefreshLimits.interval[0]}
	max={ventoraIdleRefreshLimits.interval[1]}
	icon="fas fa-clock"
	validation={refreshIntervalValidation}
	onInput={(e) => inputField('interval', e)}
	onBlur={(e) => blurField('interval', e)}
/>

<VentoraFanNumericInput
	_id="input-ventora-refresh-duration"
	labelKey="ventora_refresh_duration"
	labelDefault="Refresh duration (seconds)"
	hintKey={VENTORA_REFRESH_DURATION_HINT.key}
	hintDefault={VENTORA_REFRESH_DURATION_HINT.default}
	value={draft.duration}
	min={ventoraIdleRefreshLimits.duration[0]}
	max={ventoraIdleRefreshLimits.duration[1]}
	icon="fas fa-stopwatch"
	validation={refreshDurationValidation}
	onInput={(e) => inputField('duration', e)}
	onBlur={(e) => blurField('duration', e)}
/>

<Button
	size="s"
	width="full"
	variant="primary"
	disabled={!dirty || hasValidationErrors}
	{loading}
	on:click={save}
	id="button-ventora-idle-refresh-save"
>
	{$format('button_save', { default: 'Save' })}
</Button>
